import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { EnvelopeSimple, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Newsletter = () => {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const copy = t.newsletter || {
    overline: "Newsletter",
    title: "Pensieri lenti,\ndirettamente nella tua casella.",
    lead: "Una lettera al mese sulla vita, la gestione e il mercato del Lago di Como. Solo scritti curati, mai spam.",
    placeholder: "Il tuo indirizzo email",
    submit: "Iscrivimi",
    success: "Grazie. Ci sentiamo presto.",
    error: "Qualcosa non ha funzionato. Riprova.",
    disclaimer: "Uso solo per l'invio della newsletter. Nessuna condivisione con terzi.",
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/newsletter`, { email, locale: lang });
      toast.success(copy.success);
      setEmail("");
      setStatus("success");
      setTimeout(() => setStatus("idle"), 3500);
    } catch {
      toast.error(copy.error);
      setStatus("idle");
    }
  };

  return (
    <section
      className="relative bg-[#232326] text-[#F1EEE8] py-20 md:py-28 overflow-hidden"
      data-testid="newsletter-section"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          background:
            "radial-gradient(ellipse at 15% 30%, rgba(19,83,90,0.7), transparent 55%), radial-gradient(ellipse at 85% 70%, rgba(184,143,92,0.35), transparent 60%)",
        }}
      />
      <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
          className="md:col-span-7"
        >
          <div className="flex items-center gap-3 text-[#DDD4B9] text-[0.68rem] tracking-[0.28em] uppercase font-medium mb-6">
            <span className="w-6 h-px bg-[#B08F5C]" />
            <span>{copy.overline}</span>
          </div>
          <h2 className="font-display font-medium leading-[1.02] tracking-tight text-[clamp(2rem,4.4vw,3.6rem)] whitespace-pre-line mb-4">
            {copy.title}
          </h2>
          <p className="text-[#F1EEE8]/70 leading-relaxed text-[1rem] max-w-xl">
            {copy.lead}
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.7, 0, 0.2, 1] }}
          onSubmit={submit}
          className="md:col-span-5"
          data-testid="newsletter-form"
        >
          <div className="relative">
            <EnvelopeSimple
              size={18}
              weight="light"
              className="absolute left-4 top-1/2 -translate-y-1/2 text-[#F1EEE8]/60"
            />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={copy.placeholder}
              className="w-full bg-[#F1EEE8]/[0.05] border border-[#F1EEE8]/25 focus:border-[#B08F5C] pl-12 pr-4 py-4 text-[#F1EEE8] placeholder:text-[#F1EEE8]/45 outline-none transition-colors text-[0.98rem]"
              data-testid="newsletter-email"
            />
          </div>
          <button
            type="submit"
            disabled={status === "sending"}
            className="mt-4 inline-flex items-center justify-center gap-3 w-full bg-[#B08F5C] hover:bg-[#8A6F45] text-[#232326] font-medium text-[0.7rem] tracking-[0.24em] uppercase py-4 transition-all duration-400 disabled:opacity-60"
            data-testid="newsletter-submit"
          >
            {status === "sending" ? "…" : copy.submit}
            <ArrowRight size={14} weight="bold" />
          </button>
          <p className="mt-4 text-[0.7rem] tracking-wide text-[#F1EEE8]/45 leading-relaxed">
            {copy.disclaimer}
          </p>
        </motion.form>
      </div>
    </section>
  );
};
